import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { ArrowRight, Clock, X, TrendingUp, Lightbulb, Target, Zap, ChevronRight } from 'lucide-react';

const blogPosts = [
  {
    id: 1,
    category: 'Marketing Digital',
    title: '5 Estrategias de Video Marketing que Triplicarán tu Engagement en 2025',
    excerpt: 'Descubre cómo las marcas líderes están usando video corto para dominar las redes sociales.',
    readTime: '8 min',
    author: 'Orian Partners',
    image: 'https://images.unsplash.com/photo-1492619375914-88005aa9e8fb?w=800&auto=format&fit=crop',
    icon: TrendingUp,
    fullContent: `El video marketing se ha convertido en la herramienta más poderosa para captar la atención de tu audiencia en 2025. Las estadísticas son claras: los videos generan un 1200% más de compartidos que el texto y las imágenes combinados.

**1. Videos Cortos y Verticales**
Los Reels, TikToks y Shorts dominan el panorama. La clave está en captar la atención en los primeros 3 segundos. Usa hooks visuales impactantes y texto en pantalla para retener a tu audiencia.

**2. Contenido Detrás de Cámaras**
La autenticidad vende. Muestra el proceso creativo, tu equipo trabajando, los errores y las victorias. Esto humaniza tu marca y crea conexión emocional.

**3. Testimonios en Video**
Un cliente satisfecho hablando de tu producto es 10 veces más convincente que cualquier anuncio. Facilita que tus clientes graben testimonios simples desde su celular.

**4. Video Educativo**
Enseña algo valioso en 60 segundos. Los tutoriales rápidos posicionan tu marca como experta y generan guardados, lo que el algoritmo ama.

**5. Live Streaming**
Las transmisiones en vivo tienen 6 veces más interacciones que los videos pregrabados. Usa lives para lanzamientos, Q&A, o mostrar eventos exclusivos.

La consistencia es clave: publica al menos 3-4 videos por semana para que el algoritmo te favorezca.`,
  },
  {
    id: 2,
    category: 'Branding',
    title: 'Psicología del Color: Cómo Elegir la Paleta Perfecta para tu Marca',
    excerpt: 'Los colores comunican más que palabras. Aprende a usar la ciencia del color para conectar.',
    readTime: '6 min',
    author: 'Orian Partners',
    image: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=800&auto=format&fit=crop',
    icon: Lightbulb,
    fullContent: `El color es el primer elemento que tu audiencia percibe de tu marca. Estudios demuestran que el 90% de las decisiones de compra se basan en el color.

**El Significado de los Colores**

• **Dorado/Oro**: Lujo, éxito, prestigio. Perfecto para marcas premium que quieren transmitir exclusividad.

• **Negro**: Elegancia, poder, sofisticación. Ideal para marcas de moda, tecnología y servicios profesionales.

• **Azul**: Confianza, seguridad, profesionalismo. El favorito de bancos, tecnología y salud.

• **Rojo**: Pasión, urgencia, energía. Efectivo para llamados a la acción y ofertas.

• **Verde**: Naturaleza, crecimiento, salud. Perfecto para marcas ecológicas y wellness.

**Cómo Elegir tu Paleta**

1. Define la personalidad de tu marca (¿eres audaz o sereno?)
2. Investiga a tu competencia (¿quieres diferenciarte o alinearte?)
3. Considera tu audiencia (la edad y cultura influyen en la percepción)
4. Prueba combinaciones (usa la regla 60-30-10)

**La Regla 60-30-10**
60% color dominante, 30% color secundario, 10% color de acento. Esta proporción crea armonía visual y jerarquía clara.`,
  },
  {
    id: 3,
    category: 'Redes Sociales',
    title: 'El Algoritmo de Instagram en 2025: Lo que Realmente Funciona',
    excerpt: 'Revelamos los patrones que el algoritmo premia y cómo aprovecharlo para tu negocio.',
    readTime: '10 min',
    author: 'Orian Partners',
    image: 'https://images.unsplash.com/photo-1611162616305-c69b3fa7fbe0?w=800&auto=format&fit=crop',
    icon: Target,
    fullContent: `Instagram ha evolucionado y su algoritmo en 2025 prioriza el contenido que genera interacción genuina. Aquí te explicamos cómo aprovecharlo.

**Lo que el Algoritmo Premia**

1. **Tiempo de visualización**: Si alguien ve tu video completo, Instagram lo muestra a más personas.

2. **Guardados y Compartidos**: Valen más que los likes. Crea contenido tan útil que la gente quiera guardarlo.

3. **Comentarios largos**: Las conversaciones reales en los comentarios disparan tu alcance.

4. **Consistencia**: Publicar 1 vez al día es mejor que 7 veces en un día y luego desaparecer.

**Estrategias que Funcionan**

• **Carruseles educativos**: Tienen 3x más alcance que fotos individuales.

• **Reels con trending audio**: El algoritmo favorece contenido que usa audios populares.

• **Historias interactivas**: Usa encuestas, preguntas y sliders para aumentar engagement.

• **Responde en los primeros 30 minutos**: La actividad temprana determina el alcance del post.

**Errores Comunes**

❌ Usar hashtags prohibidos o spam
❌ Publicar y desaparecer (el algoritmo detecta esto)
❌ Comprar seguidores (destruye tu engagement rate)
❌ Ignorar los DMs y comentarios`,
  },
  {
    id: 4,
    category: 'Automatización',
    title: 'IA para Pequeños Negocios: Herramientas que Puedes Usar Hoy',
    excerpt: 'No necesitas un gran presupuesto para implementar IA. Estas herramientas transformarán tu negocio.',
    readTime: '7 min',
    author: 'Orian Partners',
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=800&auto=format&fit=crop',
    icon: Zap,
    fullContent: `La inteligencia artificial ya no es exclusiva de grandes corporaciones. Hoy puedes automatizar tareas que te roban horas cada semana.

**Herramientas de IA Esenciales**

**1. ChatGPT / Claude para Contenido**
Genera borradores de posts, emails, y descripciones de productos. Ahorra 5+ horas semanales en copywriting.

**2. Canva con IA**
Crea diseños profesionales en minutos. La función "Magic Write" genera textos y "Background Remover" edita fotos automáticamente.

**3. Notion AI**
Organiza tu negocio y genera resúmenes, traducciones y planes de acción automáticamente.

**4. Zapier / Make**
Conecta tus aplicaciones para que trabajen juntas. Ejemplo: nuevo lead en formulario → email automático → tarea en CRM.

**5. ManyChat**
Chatbots para Instagram y WhatsApp que responden preguntas frecuentes 24/7.

**Cómo Empezar**

1. Identifica tareas repetitivas que haces cada semana
2. Busca una herramienta de IA que las automatice
3. Empieza con la versión gratuita
4. Mide el tiempo que ahorras

**ROI de la Automatización**
Un chatbot bien configurado puede manejar el 70% de las consultas de clientes. Eso son horas que puedes dedicar a vender y crear.`,
  },
];

const categories = ['Todos', 'Marketing Digital', 'Branding', 'Redes Sociales', 'Automatización'];

export default function Blog() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [selectedPost, setSelectedPost] = useState(null);

  const filteredPosts = activeCategory === 'Todos' 
    ? blogPosts 
    : blogPosts.filter(post => post.category === activeCategory);

  return (
    <section
      id="blog"
      ref={ref}
      className="relative py-24 md:py-32 bg-[#050505] overflow-hidden"
      data-testid="blog-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12"
        >
          <div>
            <span className="text-xs font-mono tracking-[0.3em] uppercase text-[#D4AF37] mb-4 block">
              Knowledge Hub
            </span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white">
              Ideas &
              <br />
              <span className="gold-text">Recursos</span>
            </h2>
          </div>
          <p className="text-zinc-400 max-w-md text-sm md:text-base">
            Estrategias, tendencias y consejos de marketing que puedes aplicar hoy en tu negocio.
          </p>
        </motion.div>

        {/* Categories */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex flex-wrap gap-3 mb-12"
        >
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 text-xs tracking-wider uppercase transition-all duration-300 ${
                activeCategory === cat
                  ? 'bg-[#D4AF37] text-black'
                  : 'bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
            >
              {cat}
            </button>
          ))}
        </motion.div>

        {/* Blog Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {filteredPosts.map((post, index) => (
            <motion.article
              key={post.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 + index * 0.1 }}
              onClick={() => setSelectedPost(post)}
              className="group relative bg-zinc-950 border border-white/5 hover:border-[#D4AF37]/30 transition-all duration-500 overflow-hidden cursor-pointer"
              data-testid={`blog-post-${index}`}
            >
              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/50 to-transparent" />
                <div className="absolute top-4 left-4">
                  <span className="px-3 py-1 bg-[#D4AF37]/90 text-black text-xs font-bold tracking-wider uppercase">
                    {post.category}
                  </span>
                </div>
                <div className="absolute top-4 right-4 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center">
                  <post.icon size={18} className="text-[#D4AF37]" />
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-lg font-bold text-white group-hover:text-[#D4AF37] transition-colors duration-300 mb-3">
                  {post.title}
                </h3>
                <p className="text-sm text-zinc-500 leading-relaxed mb-4">
                  {post.excerpt}
                </p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-[#D4AF37] font-medium">{post.author}</span>
                    <div className="flex items-center gap-2 text-xs text-zinc-600">
                      <Clock size={12} />
                      <span>{post.readTime}</span>
                    </div>
                  </div>
                  <span className="flex items-center gap-1 text-xs text-[#D4AF37] opacity-0 group-hover:opacity-100 transition-opacity">
                    Leer más <ChevronRight size={14} />
                  </span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      {/* Article Modal */}
      <AnimatePresence>
        {selectedPost && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={() => setSelectedPost(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="relative bg-zinc-950 border border-white/10 max-w-3xl w-full max-h-[90vh] overflow-y-auto my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setSelectedPost(null)}
                className="absolute top-4 right-4 z-10 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:text-[#D4AF37] transition-colors"
              >
                <X size={20} />
              </button>

              <div className="relative h-64 overflow-hidden">
                <img src={selectedPost.image} alt={selectedPost.title} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 to-transparent" />
                <div className="absolute bottom-6 left-6">
                  <span className="px-3 py-1 bg-[#D4AF37] text-black text-xs font-bold tracking-wider uppercase">
                    {selectedPost.category}
                  </span>
                </div>
              </div>

              <div className="p-8">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">{selectedPost.title}</h2>
                <div className="flex items-center gap-4 mb-8 text-sm">
                  <span className="text-[#D4AF37]">{selectedPost.author}</span>
                  <span className="text-zinc-600">•</span>
                  <span className="text-zinc-500">{selectedPost.readTime} de lectura</span>
                </div>
                <div className="prose prose-invert prose-gold max-w-none">
                  {selectedPost.fullContent.split('\n\n').map((paragraph, i) => (
                    <p key={i} className="text-zinc-300 leading-relaxed mb-4 whitespace-pre-line">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
